// v2 avenue — a pale concrete slab road: a light concrete deck with dark charcoal
// gutter strips along its open outer edges, deep amber centre markings and a
// terracotta crossing when the tile is a crosswalk. Ported 1:1 from age-of-mcp's
// v2/avenue.ts. The model library has no terrain and the per-tile network params
// (connections / crosswalk / heights) are not modelled here, so this renders the
// default lone-tile look the flattened file bakes: flat ground (heights flat ->
// the source's `warpedSlab` reduces to the box slab inlined as `slab`), no road
// connections (connections = 0 -> all four edges are open, so all four gutters
// show and there are no joint grooves or centre markings) and no crosswalk.
// Three build stages: roadbed, bare deck, and the gutter-edged deck (stage-2
// equals the complete look once connections are 0, so it is folded in).
import * as THREE from "three";
import { defineModel, type Part } from "../../lib";

// Pale-concrete palette with dark edges — the inverse of the classic scheme.
const CONCRETE = "#9a988f";   // poured concrete deck
const ROADBED  = "#7a7468";   // construction-stage gravel sub-base
const GUTTER   = "#3d3d40";   // dark gutter strip on open outer edges

const HALF   = 0.5;
const SLAB_H = 0.12;    // deck thickness — base buried in the ground
const LIFT   = 0.025;   // proud of the terrain — keep = renderer AVENUE_LIFT
const EPS    = 0.004;   // markings sit this sliver above the deck

type V = readonly [number, number, number];
function quad(out: number[], p0: V, p1: V, p2: V, p3: V): void {
  out.push(...p0, ...p1, ...p2, ...p0, ...p2, ...p3);
}

// A box slab whose top sits at y = `lift` (flat ground), bottom at -thickness —
// the flat reduction of the source's `warpedSlab` on flat corners.
function slab(thickness: number, color: string, lift: number): Part {
  const top = lift, yb = -thickness, h = HALF;
  const tSW: V = [-h, top, -h], tSE: V = [h, top, -h];
  const tNW: V = [-h, top,  h], tNE: V = [h, top,  h];
  const bSW: V = [-h, yb, -h], bSE: V = [h, yb, -h];
  const bNW: V = [-h, yb,  h], bNE: V = [h, yb,  h];
  const pos: number[] = [];
  quad(pos, tSW, tNW, tNE, tSE);   // top    (+Y)
  quad(pos, bSW, bSE, bNE, bNW);   // bottom (-Y)
  quad(pos, tNW, bNW, bNE, tNE);   // north  (+Z)
  quad(pos, tSE, bSE, bSW, tSW);   // south  (-Z)
  quad(pos, tSE, tNE, bNE, bSE);   // east   (+X)
  quad(pos, tSW, bSW, bNW, tNW);   // west   (-X)
  const geometry = new THREE.BufferGeometry();
  geometry.setAttribute("position", new THREE.Float32BufferAttribute(pos, 3));
  return { geometry, color, position: [0, 0, 0] };
}

// Lay a marking flush ON the flat deck at local (lx, lz): the box is deck-deep
// so its body is buried and only its top stands EPS proud.
function mark(lx: number, lz: number, sx: number, sz: number, color: string): Part {
  const h = SLAB_H + EPS;
  return {
    geometry: new THREE.BoxGeometry(sx, h, sz),
    color,
    position: [lx, LIFT + EPS - h / 2, lz],
    eulerOrder: "ZYX",
  };
}

// Dark gutter strips on every open outer edge — with no road connections, all
// four sides are open, so all four gutters show.
function gutterStrips(): Part[] {
  const w = 0.075, o = 0.452;
  return [
    mark(0, o, HALF * 2, w, GUTTER),
    mark(0, -o, HALF * 2, w, GUTTER),
    mark(o, 0, w, HALF * 2, GUTTER),
    mark(-o, 0, w, HALF * 2, GUTTER),
  ];
}

export const avenueV2 = defineModel({
  id: "avenue-v2",
  tiles: { w: 1, h: 1 },
  batchId: "fable",
  name: "Avenue (v2)",
  tags: ["building","avenue","low-poly","age-of-mcp","fable"],
  summary: "Low-poly avenue.",
  description: "A road tile for vehicle traffic.",
  author: "age-of-mcp",
  footprint: 0.5,
  defaultVariant: "complete",
  variants: [
    { id: "stage-0", label: "Foundation", parts: () => [slab(0.1, ROADBED, LIFT * 0.6)] },
    { id: "stage-1", label: "Lower", parts: () => [slab(SLAB_H, CONCRETE, LIFT)] },
    {
      id: "complete", label: "Complete",
      parts: () => [slab(SLAB_H, CONCRETE, LIFT), ...gutterStrips()],
    },
  ],
  animations: [],
});
